/**
 * horsDuCorps — ce qui change quand une forme est écrite dans un en-tête, un
 * pied de page ou une note de bas de page, et rien d'autre.
 *
 * Le LIEU est une dimension de la cible (`canvasTarget.ts`), pas un verbe :
 * `format_text`, `format_paragraphs` et la lecture de forme y gardent leur
 * résolution de cible, leurs réglages, leur relecture et leur reçu. Deux choses
 * seulement diffèrent du corps, et ce module les porte seul.
 *
 * 1. **Les paragraphes visés.** {@link acteVise} rend les paragraphes du lieu
 *    sous la forme que les cibles visent : la résolution de cible, la recherche d'un texte
 *    exact, la vérification et le reçu le lisent comme ils lisent le corps.
 *    Chaque paragraphe y porte sa partie (`lecteurDeForme.ts`).
 * 2. **La manière d'écrire.** L'éditeur refuse un plan atomique hors du corps
 *    (« v2 mutation plan engine supports only the body story »,
 *    `CAPABILITY_UNSUPPORTED`, à l'aperçu comme à l'application). Une étape de
 *    plan dont le paragraphe porte une partie reçoit donc son ADRESSE
 *    ({@link adresseHorsDuCorps}), et {@link ecritureDirecteDansUnLieu} — la
 *    manière d'écrire que le socle (`ecritureDeForme.ts`) choisit pour un lieu —
 *    l'écrit seule, par l'opération directe ({@link ecrituresDirectes}), après
 *    l'avoir aperçue sur le texte que l'éditeur lit dans la story
 *    ({@link apercuHorsDuCorps}). Ce que cette manière lit et écrit dans
 *    l'éditeur est ici, et nulle part ailleurs.
 *
 * ## Ce qui est mesuré
 *
 * `tests/ws-mocked/mesure-socle-lieux-par-plan.spec.ts` et les sondes
 * `mesure-socle-entetes-et-champs`, `mesure-socle-notes` (SuperDoc 2.15.1,
 * 2026-10-06, suivi coupé, écritures en `changeMode:'direct'`) :
 *
 * - `format.apply` n'accepte hors plan qu'une SÉLECTION (`target must be a
 *   SelectionTarget object`) : deux positions dans le texte d'un bloc, et la
 *   story. Ni `scope`, ni `alignment` (« Allowed fields: target, ref, inline,
 *   in »). Sans la story, le bloc est cherché dans le corps
 *   (`TARGET_NOT_FOUND`).
 * - `format.paragraph.setAlignment`, `setIndentation`, `setSpacing`,
 *   `setBorder`, `clearBorder`, `resetDirectFormatting` et
 *   `styles.paragraph.setStyleRef` prennent un bloc qui porte sa story.
 * - Les deux honorent `expectedRevision` (`REVISION_MISMATCH` RENDU, rien
 *   d'écrit), rendent `NO_OP` quand rien ne change, et font avancer la révision
 *   du document de 1 par écriture, avec une entrée d'historique chacune.
 * - `blocks.list({in: story, includeText:true})` rend le texte de chaque bloc
 *   de la story, dans l'espace des positions de `format.apply` ; une story que
 *   le document ne porte pas rend zéro bloc. Le `nodeId` d'un bloc est le
 *   `w14:paraId` du fichier.
 * - Les champs `PAGE` et `NUMPAGES` d'un pied ne comptent pour aucun caractère,
 *   ni dans ce texte ni dans celui du lecteur de forme.
 *
 * - `blocks.list({in: story})` rend aussi `revision`, la révision du document à
 *   laquelle la liste est lue ; l'appel coûte 0,2 à 0,3 ms sur un en-tête de deux
 *   paragraphes ou une note, contre 0,4 ms pour `doc.info`, et `history.get`
 *   0,03 ms (relevé « preuve » de `mesure-socle-lieux-par-plan`, 2026-10-06).
 * - Une frappe dans un paragraphe d'en-tête (insertion de texte par
 *   `doc.insert` sur une sélection vide dans la story) fait avancer la révision
 *   de 1 et laisse une entrée d'historique ; une écriture soumise ensuite sous
 *   la révision d'avant est refusée (`REVISION_MISMATCH`, rendu, rien d'écrit).
 *
 * ## Ce que le lieu coûte, faute de plan
 *
 * Une entrée d'historique par appel direct — par paragraphe, ou par passage —
 * au lieu d'une par lot : le reçu les compte (`format.writes`), et c'est ce
 * nombre qu'une annulation défait. Aucune atomicité : une étape refusée laisse
 * les précédentes écrites.
 *
 * ## Une frappe de l'avocat entre deux écritures
 *
 * Un plan du corps est gardé par la révision que le précédent a rendue. Une
 * écriture directe ne rend pas la sienne : la relire après coup et l'adopter
 * absorberait une frappe de l'avocat venue entre deux écritures, et la suivante
 * partirait sur des positions lues à l'aperçu — sur les mauvais caractères.
 *
 * Chaque appel direct est donc précédé de sa PREUVE (`prouverLePassage`) : la
 * story est relue — un appel, qui rend le texte de ses paragraphes et la
 * révision à laquelle il est lu —, le texte du paragraphe visé doit être celui
 * de l'aperçu, et l'écriture part sous cette révision. C'est la preuve la moins
 * chère : elle remplace la lecture de révision qui suivait chaque écriture, à
 * nombre d'appels égal. Trois cas :
 * - le texte du paragraphe a changé, ou il n'est plus dans sa story : la série
 *   s'arrête là. Ce qui est écrit reste, le reste n'est pas tenté, et le reçu
 *   (`partial`) nomme le lieu, ce qui est écrit, ce qui ne l'est pas, et demande
 *   de relire le lieu ;
 * - la frappe tombe entre la preuve et l'écriture : l'éditeur refuse l'écriture
 *   (`REVISION_MISMATCH`), même arrêt ;
 * - la frappe est ailleurs — un autre paragraphe, le corps : les positions à
 *   écrire n'ont pas bougé, la série continue. L'historique porte alors une
 *   entrée de plus que le reçu ne compte d'écritures : le socle le voit à la
 *   clôture et le reçu dit que l'annulation par compte n'est pas sûre
 *   (`ecritureDeForme.ts`, « Ce qui compte comme une écriture »).
 *
 * Une écriture par paragraphe (`ecrireUnParagraphe` : retraits, espacement,
 * bordure, style) vise un bloc par son identifiant, jamais une position : elle
 * n'a pas besoin de cette preuve, et suit la règle du corps. Reste une fenêtre,
 * que rien ne ferme : une frappe entre la DERNIÈRE écriture de la série et la
 * lecture de sa révision, qui la suit aussitôt.
 */

import type { CanvasFormatPlace } from '@hector/shared-types';
import { estObjet } from './apercuDePlan';
import type { ManiereDEcrire, OutilsDuSocle, SortDUnLot } from './ecritureDeForme';
import { estArretDeLot } from './executionAsynchrone';
import { marquerRevisionDInfo, muterSousFences, type RevisionDeDocument } from './fencesDeMutation';
import { corpsAvecSesCellules, type ActeVise, type FormeDeLActe, type FormeDeParagraphe, type ParagrapheDeTableau, type ParagrapheVise, type PartieHorsDuCorps } from '@hector/docx-forme';
import type { EtapeDePlan } from './planDeFormatText';
import type { RefusDeLEditeur } from './recuDEcritureDeForme';
import { suiteDUnRefusALApplication, type EtapeRefusee } from './repliDeLot';
import type { CanvasActionContext } from './textActionExecutors';

/** L'adresse d'une partie pour l'éditeur. */
export type StoryDeLEditeur =
  | { kind: 'story'; storyType: 'headerFooterPart'; refId: string }
  | { kind: 'story'; storyType: 'footnote'; noteId: string };

export const storyDeLaPartie = (partie: PartieHorsDuCorps): StoryDeLEditeur => (
  partie.genre === 'footnote'
    ? { kind: 'story', storyType: 'footnote', noteId: partie.noteId }
    : { kind: 'story', storyType: 'headerFooterPart', refId: partie.refId }
);

/** Une clé par story, pour ne lire chacune qu'une fois. */
export const cleDeStory = (story: StoryDeLEditeur): string => (story.storyType === 'footnote' ? `footnote:${story.noteId}` : `part:${story.refId}`);

const ZONE_EN_CLAIR = { primary: 'primary', first_page: 'first-page', even_pages: 'even-pages' } as const;

/** Le lieu d'un paragraphe lu hors du corps, tel qu'une cible le reprend dans `in`. */
export function lieuDeLaPartie(partie: PartieHorsDuCorps): CanvasFormatPlace {
  return partie.genre === 'footnote' ? { kind: 'footnote', note_id: partie.noteId } : { kind: partie.genre, zone: partie.zone };
}

/** Vrai si la partie est de ce lieu : sa zone, sa note — ou une note quelconque quand le lieu est « toutes les notes ». */
function estDuLieu(partie: PartieHorsDuCorps, lieu: CanvasFormatPlace): boolean {
  const sien = lieuDeLaPartie(partie);
  if (sien.kind === 'footnote') return lieu.kind === 'footnote' && (lieu.note_id === undefined || lieu.note_id === sien.note_id);
  return lieu.kind === sien.kind && lieu.zone === sien.zone;
}

/** Un lieu, tel que le reçu le nomme au modèle ; sans lieu, le corps. */
export function direLeLieu(lieu: CanvasFormatPlace | undefined): string {
  if (lieu === undefined) return 'the body of the act';
  if (lieu.kind === 'footnote') return lieu.note_id === undefined ? 'the footnotes' : `footnote ${lieu.note_id}`;
  return `the ${ZONE_EN_CLAIR[lieu.zone]} ${lieu.kind}`;
}

/** Les lieux que le fichier porte, pour qu'un refus dise lesquels viser. */
function direLesLieuxPresents(acte: FormeDeLActe): string {
  const zones = acte.horsDuCorps.flatMap(({ partie }) => (partie.genre === 'footnote' ? [] : [`${ZONE_EN_CLAIR[partie.zone]} ${partie.genre}`]));
  const notes = acte.horsDuCorps.flatMap(({ partie }) => (partie.genre === 'footnote' ? [partie.noteId] : []));
  const presents = [...new Set(zones), ...(notes.length === 0 ? [] : [`footnotes ${notes.slice(0, 30).join(', ')}${notes.length > 30 ? `, and ${notes.length - 30} more` : ''}`])];
  return presents.length === 0 ? 'This document has no header, footer or footnote.' : `This document has: ${presents.join(', ')}.`;
}

/**
 * Ce qu'une cible vise dans l'acte lu : sans lieu, le corps avec ses
 * paragraphes de cellule de tableau à leur place (`corpsAvecSesCellules`) ;
 * avec un lieu, les paragraphes de l'en-tête, du pied ou des notes — ce que la
 * cible « tout » y désigne, et ce où un texte exact y est cherché. Les tableaux
 * d'un LIEU restent à part, comptés et non écrits (`cellulesNonVisees`,
 * `table_cell`) : ce que l'éditeur y accepte n'est pas mesuré.
 *
 * Une zone que plusieurs parties tiennent (une par section) et « toutes les
 * notes » réunissent leurs paragraphes dans l'ordre du fichier. Un identifiant
 * déjà porté par un paragraphe du même lieu est rendu NUL : la vérification
 * retrouve un paragraphe par son identifiant, et deux parties peuvent en porter
 * un même. Ce paragraphe garde sa raison (`identifiantEnDouble`) : il est compté
 * sans être écrit, sous `duplicate_node_id` — pas comme un paragraphe que le
 * fichier ne nomme pas.
 *
 * @param acte L'acte entier, tel que le lecteur le rend.
 * @returns La vue visée, ou `absent` : la phrase qui dit que le document ne
 *   porte pas ce lieu, et lesquels il porte.
 */
export function acteVise(acte: FormeDeLActe, lieu: CanvasFormatPlace | undefined): ActeVise | { absent: string } {
  if (lieu === undefined) return corpsAvecSesCellules(acte);
  const parties = acte.horsDuCorps.filter(({ partie }) => estDuLieu(partie, lieu));
  if (parties.length === 0) return { absent: `this document has no ${direLeLieu(lieu).replace(/^the /, '')} (looked for ${direLeLieu(lieu)}). ${direLesLieuxPresents(acte)}` };
  const vus = new Set<string>();
  const unique = <Paragraphe extends FormeDeParagraphe>(lu: Paragraphe): Paragraphe => {
    if (lu.nodeId === null) return lu;
    if (vus.has(lu.nodeId)) return { ...lu, nodeId: null, identifiantEnDouble: lu.nodeId };
    vus.add(lu.nodeId);
    return lu;
  };
  const paragraphes: ParagrapheVise[] = [];
  const cellulesNonVisees: ParagrapheDeTableau[] = [];
  for (const partie of parties) {
    const avant = paragraphes.length;
    paragraphes.push(...partie.paragraphes.map((lu) => ({ ...unique(lu), enCellule: false })));
    cellulesNonVisees.push(...partie.paragraphesDeTableau.map((cellule) => unique({ ...cellule, rang: avant + cellule.rang })));
  }
  return { paragraphes, cellulesNonVisees, stylesDeParagraphe: acte.stylesDeParagraphe };
}

/** Un paragraphe d'une story, tel que l'éditeur le liste : son identifiant et son texte. */
export interface BlocDeStory {
  nodeId: string;
  texte: string;
}

/** Les stories du lieu dont un paragraphe au moins n'a pas d'identifiant au fichier, chacune une fois. */
export function storiesSansIdentifiant(acte: ActeVise): StoryDeLEditeur[] {
  const stories = new Map<string, StoryDeLEditeur>();
  for (const { nodeId, partie } of acte.paragraphes) {
    if (nodeId === null && partie !== undefined) stories.set(cleDeStory(storyDeLaPartie(partie)), storyDeLaPartie(partie));
  }
  return [...stories.values()];
}

/**
 * Donne aux paragraphes du lieu que le fichier ne nomme pas l'identifiant que
 * l'éditeur leur donne, par leur RANG dans leur story.
 *
 * WHY : `footnotes.update` — la réécriture d'une note par `edit_footnote` —
 * rend un paragraphe sans `w14:paraId` au fichier, que l'éditeur nomme
 * `__sd:paragraph:N` (`mesure-socle-lieux-par-plan`, 2026-10-06). Sans cela,
 * une note réécrite ne se mettrait plus en forme : son paragraphe serait compté
 * `no_node_id`, comme tout paragraphe qu'aucune écriture n'adresse.
 *
 * Le rang seul ne prouve rien : une story n'est nommée que si l'éditeur y liste
 * autant de paragraphes que le fichier, le même texte à chaque rang, et les
 * mêmes identifiants là où le fichier en porte. Sinon elle est laissée telle
 * quelle.
 *
 * Un identifiant ne nomme jamais deux paragraphes du lieu : la vérification
 * retrouve un paragraphe par lui, et jugerait l'un pour l'autre. Les stories
 * sont nommées l'une après l'autre, et celle qui redonnerait un identifiant
 * déjà porté — par le fichier, par une story nommée avant elle, ou deux fois en
 * elle-même — est laissée telle quelle. Ce qui est MESURÉ de l'unicité entre
 * stories (relevé « preuve » de `mesure-socle-lieux-par-plan`, 2026-10-06) :
 * trois notes réécrites reçoivent trois identifiants distincts
 * (`__sd:paragraph:2`, `:3`, `:4`, dans l'ordre des notes et non des
 * réécritures). Ce seul cas ne fait pas une règle de l'éditeur : l'unicité
 * n'est pas supposée, elle est tenue ici.
 *
 * @param acte Ce qu'une cible vise dans un lieu ({@link acteVise}).
 * @param blocs Par story ({@link cleDeStory}), ses paragraphes hors tableaux, dans l'ordre de l'éditeur.
 */
export function identifierParLeRang(acte: ActeVise, blocs: ReadonlyMap<string, readonly BlocDeStory[]>): ActeVise {
  const texteDe = (lu: FormeDeParagraphe): string => lu.passages.map((passage) => passage.texte).join('');
  const cleDuParagraphe = (lu: FormeDeParagraphe): string | undefined => (lu.partie === undefined ? undefined : cleDeStory(storyDeLaPartie(lu.partie)));
  // Les identifiants déjà portés dans le lieu : ceux du fichier, puis ceux de chaque story nommée.
  const pris = new Set(acte.paragraphes.flatMap((lu) => (lu.nodeId === null ? [] : [lu.nodeId])));
  const prouvees = new Map<string, readonly BlocDeStory[]>();
  for (const [cle, liste] of blocs) {
    const duFichier = acte.paragraphes.filter((lu) => cleDuParagraphe(lu) === cle);
    const donnes = new Set<string>();
    const concorde = duFichier.length === liste.length && duFichier.every((lu, rang) => {
      const bloc = liste[rang];
      if (bloc === undefined || bloc.texte !== texteDe(lu)) return false;
      if (lu.nodeId !== null) return lu.nodeId === bloc.nodeId;
      if (pris.has(bloc.nodeId) || donnes.has(bloc.nodeId)) return false;
      donnes.add(bloc.nodeId);
      return true;
    });
    if (!concorde) continue;
    prouvees.set(cle, liste);
    for (const nodeId of donnes) pris.add(nodeId);
  }
  const rangs = new Map<string, number>();
  return {
    ...acte,
    paragraphes: acte.paragraphes.map((lu) => {
      const cle = cleDuParagraphe(lu);
      if (cle === undefined) return lu;
      const rang = rangs.get(cle) ?? 0;
      rangs.set(cle, rang + 1);
      const bloc = prouvees.get(cle)?.[rang];
      return lu.nodeId !== null || bloc === undefined ? lu : { ...lu, nodeId: bloc.nodeId };
    }),
  };
}

/** Où écrire une étape hors du corps : la story, le bloc, et le passage quand l'étape n'en vise qu'un. */
export interface AdresseHorsDuCorps {
  story: StoryDeLEditeur;
  nodeId: string;
  /** Absente : tout le texte du bloc. */
  plage?: { debut: number; fin: number };
}

/**
 * L'adresse à joindre à l'étape de plan d'un paragraphe : rien pour un
 * paragraphe du corps, dont l'étape part dans un plan.
 */
export function adresseHorsDuCorps(
  paragraphe: Pick<FormeDeParagraphe, 'partie'> & { nodeId: string },
  plage?: { debut: number; fin: number },
): { horsDuCorps?: AdresseHorsDuCorps } {
  if (paragraphe.partie === undefined) return {};
  return { horsDuCorps: { story: storyDeLaPartie(paragraphe.partie), nodeId: paragraphe.nodeId, ...(plage === undefined ? {} : { plage: { debut: plage.debut, fin: plage.fin } }) } };
}

/** Un bloc tel qu'une écriture hors plan le vise : sans story, l'éditeur le cherche dans le corps. */
const blocVise = (nodeId: string, story: StoryDeLEditeur | undefined): Record<string, unknown> => ({ kind: 'block', nodeType: 'paragraph', nodeId, ...(story === undefined ? {} : { story }) });

/** La cible d'une écriture de paragraphe hors plan : le bloc, et sa story hors du corps. */
export function blocDeLEcriture(paragraphe: Pick<FormeDeParagraphe, 'partie'> & { nodeId: string }): Record<string, unknown> {
  return blocVise(paragraphe.nodeId, paragraphe.partie === undefined ? undefined : storyDeLaPartie(paragraphe.partie));
}

/** Ce qu'une adresse couvre du texte de son bloc : son passage, ou tout le texte. */
const etendueDeLAdresse = (adresse: AdresseHorsDuCorps, texte: string): { debut: number; fin: number } => adresse.plage ?? { debut: 0, fin: texte.length };

/**
 * Le texte que l'éditeur lit du bloc d'une étape, dans sa story ; `undefined` :
 * l'étape n'a pas d'adresse, ou l'éditeur ne liste pas ce bloc.
 *
 * @param textes Par story ({@link cleDeStory}), le texte de chaque bloc par son identifiant.
 */
function texteDeLEtape(step: EtapeDePlan, textes: ReadonlyMap<string, ReadonlyMap<string, string>>): string | undefined {
  return step.horsDuCorps === undefined ? undefined : textes.get(cleDeStory(step.horsDuCorps.story))?.get(step.horsDuCorps.nodeId);
}

/** Le refus d'une étape dont l'éditeur ne liste pas le bloc dans sa story. */
function refusDuBlocIntrouvable(step: EtapeDePlan): RefusDeLEditeur {
  const adresse = step.horsDuCorps;
  return { code: 'TARGET_NOT_FOUND', detail: `TARGET_NOT_FOUND: the editor lists no paragraph ${adresse?.nodeId ?? step.id} in ${adresse === undefined ? 'a story' : cleDeStory(adresse.story)}` };
}

/**
 * L'aperçu d'étapes hors du corps, bâti sur le texte que l'éditeur lit dans
 * chaque story : il tient lieu de l'aperçu d'un plan, que l'éditeur refuse là.
 * `rendu` a la forme que lit `resolutionsParEtape` (`apercuDePlan.ts`) — le
 * texte entier du bloc pour une étape de bloc, le texte à la position pour un
 * passage —, si bien que les deux preuves d'un passage adressé par position
 * (`planDeFormatText.ts`) s'y font comme dans le corps.
 *
 * @param textes Par story ({@link cleDeStory}), le texte de chaque bloc par son identifiant.
 */
export function apercuHorsDuCorps(
  steps: readonly EtapeDePlan[],
  textes: ReadonlyMap<string, ReadonlyMap<string, string>>,
): { rendu: unknown; gardees: EtapeDePlan[]; refusees: EtapeRefusee[] } {
  const gardees: EtapeDePlan[] = [];
  const refusees: EtapeRefusee[] = [];
  const resolues: Array<{ stepId: string; resolutions: unknown[] }> = [];
  for (const step of steps) {
    const adresse = step.horsDuCorps;
    const texte = texteDeLEtape(step, textes);
    if (adresse === undefined || texte === undefined) {
      refusees.push({ stepId: step.id, refus: refusDuBlocIntrouvable(step) });
      continue;
    }
    const { debut, fin } = etendueDeLAdresse(adresse, texte);
    gardees.push(step);
    resolues.push({ stepId: step.id, resolutions: [{ target: { blockId: adresse.nodeId }, range: { from: debut, to: fin }, text: texte.slice(debut, fin) }] });
  }
  return { rendu: { steps: resolues }, gardees, refusees };
}

/** Un appel direct à l'éditeur : la surface, et ce qu'elle reçoit. */
export interface EcritureDirecte {
  surface: 'format.apply' | 'format.paragraph.setAlignment';
  entree: Record<string, unknown>;
}

/**
 * Les appels directs qui écrivent une étape hors du corps : la forme de texte
 * par une sélection, l'alignement par le bloc.
 *
 * @param texteDeLEditeur Le texte du bloc tel que l'éditeur le lit : une étape
 *   de bloc s'écrit sur toute sa longueur.
 */
export function ecrituresDirectes(step: EtapeDePlan, texteDeLEditeur: string): EcritureDirecte[] {
  const adresse = step.horsDuCorps;
  if (adresse === undefined) return [];
  const { story, nodeId } = adresse;
  const { debut, fin } = etendueDeLAdresse(adresse, texteDeLEditeur);
  const { inline, alignment } = step.args;
  return [
    ...(alignment === undefined ? [] : [{ surface: 'format.paragraph.setAlignment' as const, entree: { target: blocVise(nodeId, story), alignment } }]),
    ...(inline === undefined ? [] : [{
      surface: 'format.apply' as const,
      entree: { target: { kind: 'selection', start: { kind: 'text', blockId: nodeId, offset: debut }, end: { kind: 'text', blockId: nodeId, offset: fin }, story }, inline },
    }]),
  ];
}

/** Nombre de blocs demandés par lecture d'une story. Valeur de jugement : un en-tête, un pied ou une note en porte rarement plus de quelques-uns. */
const BLOCS_PAR_LECTURE_DE_STORY = 200;

type MethodeDeLEditeur = (entree?: unknown, options?: unknown) => unknown;

/** Les surfaces de l'éditeur qu'emploie l'écriture directe dans un lieu. */
interface SurfacesDuLieu {
  blocks?: { list?: MethodeDeLEditeur };
  format?: { apply?: MethodeDeLEditeur; paragraph?: { setAlignment?: MethodeDeLEditeur } };
}

/**
 * La manière d'écrire d'un LIEU — en-tête, pied, notes : chaque étape est
 * écrite seule, par l'opération directe de l'éditeur, qui refuse là tout plan
 * (voir l'en-tête). Le socle (`ecritureDeForme.ts`) la choisit à l'ouverture.
 *
 * @param lieu Le lieu que la cible vise.
 */
export function ecritureDirecteDansUnLieu({ ctx, verbe, refuser, revisionLue, refusDeLEditeur, suivi }: OutilsDuSocle, lieu: CanvasFormatPlace): ManiereDEcrire {
  // Les surfaces sont lues SUR le document à chaque appel, jamais détachées : `doc.format` rend les siennes à chaque lecture.
  const editeur = (): SurfacesDuLieu => ctx.doc as CanvasActionContext['doc'] & SurfacesDuLieu;

  /**
   * Les paragraphes d'une story dans l'ordre de l'éditeur, hors tableaux — identifiant et texte —, et la révision du
   * document à laquelle l'éditeur les a lus (il la rend avec la liste) ; `undefined` : il n'en rend pas, ou elle a
   * changé d'une page de la liste à l'autre.
   */
  const lireLaStory = async (story: StoryDeLEditeur): Promise<{ blocs: BlocDeStory[]; revision: RevisionDeDocument | undefined }> => {
    const blocs: BlocDeStory[] = [];
    let revision: RevisionDeDocument | undefined;
    for (let offset = 0; ; offset += BLOCS_PAR_LECTURE_DE_STORY) {
      const page = await editeur().blocks?.list?.({ in: story, includeText: true, offset, limit: BLOCS_PAR_LECTURE_DE_STORY });
      const lus = estObjet(page) && Array.isArray(page.blocks) ? page.blocks : [];
      const dePage = marquerRevisionDInfo(page);
      revision = offset === 0 || dePage === revision ? dePage : undefined;
      for (const bloc of lus) {
        if (estObjet(bloc) && bloc.nodeType !== 'table' && typeof bloc.nodeId === 'string' && typeof bloc.text === 'string') blocs.push({ nodeId: bloc.nodeId, texte: bloc.text });
      }
      if (lus.length < BLOCS_PAR_LECTURE_DE_STORY) return { blocs, revision };
    }
  };

  /** Les paragraphes du lieu, ceux que le fichier ne nomme pas nommés par leur rang ({@link identifierParLeRang}) ; une story illisible est laissée telle quelle. */
  const duLieu = async (entier: FormeDeLActe): Promise<{ acte: ActeVise; siLaCibleEstVide: string } | { absent: string }> => {
    const lu = acteVise(entier, lieu);
    if ('absent' in lu) return lu;
    // Une cible qui ne désigne rien dans un lieu présent : le conseil vaut pour le lieu, pas pour le corps.
    const siLaCibleEstVide = `Read ${direLeLieu(lieu)} first (canvas_inspect with formatOf {kind:"all"} and the same "in") to see what it holds. ${direLesLieuxPresents(entier)}`;
    const blocs = new Map<string, BlocDeStory[]>();
    for (const story of storiesSansIdentifiant(lu)) {
      try {
        blocs.set(cleDeStory(story), (await lireLaStory(story)).blocs);
      } catch (erreur) {
        if (estArretDeLot(erreur)) throw erreur;
      }
    }
    return { acte: blocs.size === 0 ? lu : identifierParLeRang(lu, blocs), siLaCibleEstVide };
  };

  // Le texte de chaque story, lu à l'aperçu : ce sur quoi les positions de chaque étape sont prouvées, et ce que
  // chaque écriture doit retrouver tel quel avant de partir.
  const textesDesStories = new Map<string, Map<string, string>>();
  const lireLesStories = async (steps: readonly EtapeDePlan[]): Promise<void> => {
    const stories = new Map(steps.flatMap((step) => (step.horsDuCorps === undefined ? [] : [[cleDeStory(step.horsDuCorps.story), step.horsDuCorps.story] as const])));
    for (const [cle, story] of stories) textesDesStories.set(cle, new Map((await lireLaStory(story)).blocs.map(({ nodeId, texte }) => [nodeId, texte])));
  };

  /**
   * La preuve AVANT une écriture directe : le texte du paragraphe visé, relu dans sa story, est encore celui de
   * l'aperçu. Rend la révision à laquelle il vient d'être lu — l'écriture part sous elle, si bien qu'une frappe venue
   * entre cette lecture et l'écriture la fait refuser par l'éditeur. `change` : le paragraphe n'est plus celui de
   * l'aperçu, rien ne doit y être écrit.
   */
  const prouverLePassage = async (adresse: AdresseHorsDuCorps, texteDeLApercu: string): Promise<{ revision: RevisionDeDocument } | { change: RefusDeLEditeur }> => {
    let lue = await lireLaStory(adresse.story);
    if (lue.revision === undefined) {
      // La liste ne dit pas sa révision : elle est lue d'abord, le texte ensuite. Une frappe entre les deux se voit au
      // texte, une frappe après lui fait refuser l'écriture.
      const revision = await revisionLue();
      lue = { blocs: (await lireLaStory(adresse.story)).blocs, revision };
    }
    if (lue.revision === undefined) return { change: { detail: `the editor reported no document revision before a write in ${direLeLieu(lieu)}, so that write could not be guarded` } };
    if (lue.blocs.find((bloc) => bloc.nodeId === adresse.nodeId)?.texte === texteDeLApercu) return { revision: lue.revision };
    return { change: {
      code: 'STALE_CANVAS_REVISION',
      detail: `STALE_CANVAS_REVISION: before the next write, the text of paragraph ${adresse.nodeId} in ${direLeLieu(lieu)} was no longer the one this call had read `
        + '(the lawyer typed there, or another write ran, during the call)',
    } };
  };

  return {
    surfaces: {
      'blocks.list': editeur().blocks?.list,
      'format.apply': editeur().format?.apply,
      'format.paragraph.setAlignment': editeur().format?.paragraph?.setAlignment,
    },

    viser: duLieu,

    async apercevoir(steps, nomDuPlan) {
      try {
        await lireLesStories(steps);
      } catch (erreur) {
        if (estArretDeLot(erreur)) throw erreur;
        return {
          refus: refuser({
            code: 'FORMAT_WRITE_REFUSED',
            message: `${verbe} was refused before any write: the editor could not list the paragraphs of the targeted place (${refusDeLEditeur(erreur).detail}). Nothing was written.`,
          }),
        };
      }
      if ((await revisionLue()) !== suivi.attendue) {
        return {
          refus: refuser({
            code: 'STALE_CANVAS_REVISION',
            reessayer: true,
            message: `${verbe} was refused before any write: the document changed while the ${nomDuPlan} writes were being validated `
              + '(the lawyer is typing, or another write is in flight). Nothing was written; resend the same action.',
          }),
        };
      }
      return apercuHorsDuCorps(steps, textesDesStories);
    },

    async appliquer(lots, etiquette) {
      const sorts = lots.map((): SortDUnLot => ({ ecrites: [], sansObjet: [], sautees: [] }));
      // Ni lot ni rejeu : chaque étape est sa propre écriture, dans l'ordre du document.
      const file = lots.flatMap((steps, lot) => steps.map((step) => ({ step, sort: sorts[lot] })));
      let soumises = 0;
      for (const { step, sort } of file) {
        const adresse = step.horsDuCorps;
        const texte = texteDeLEtape(step, textesDesStories);
        if (sort === undefined) continue;
        if (adresse === undefined || texte === undefined) {
          sort.sautees.push({ stepId: step.id, refus: refusDuBlocIntrouvable(step) });
          continue;
        }
        // Le sort de l'étape : celui de son dernier appel, ou du premier que l'éditeur refuse.
        let refus: RefusDeLEditeur | undefined;
        for (const { surface, entree } of ecrituresDirectes(step, texte)) {
          const preuve = await prouverLePassage(adresse, texte);
          if ('change' in preuve) {
            suivi.interruption = preuve.change;
            return sorts;
          }
          soumises += 1;
          suivi.tentee = true;
          suivi.horsPlan = false;
          const options = { changeMode: 'direct' as const, expectedRevision: preuve.revision };
          let resultat: unknown;
          try {
            // UN appel fournisseur par fermeture : un refus passager RENDU par l'éditeur (export en cours) est rejoué tel quel.
            const format = editeur().format;
            resultat = await muterSousFences(ctx, `${verbe}:${etiquette}-${soumises}`, options, () => (
              surface === 'format.apply' ? format?.apply?.(entree, options) : format?.paragraph?.setAlignment?.(entree, options)
            ));
          } catch (erreur) {
            if (estArretDeLot(erreur)) throw erreur;
            refus = refusDeLEditeur(erreur);
            break;
          }
          if (!estObjet(resultat) || resultat.success !== true) {
            refus = refusDeLEditeur(resultat);
            break;
          }
          // Une écriture directe appliquée est une entrée d'historique (`mesure-socle-lieux-par-plan`).
          suivi.ecritures += 1;
        }
        if (refus === undefined) {
          sort.ecrites.push(step.id);
          continue;
        }
        const suite = suiteDUnRefusALApplication([step], refus);
        if (suite.issue === 'arreter') {
          // Le document a changé entre la preuve et l'écriture : l'éditeur l'a refusée, rien n'est écrit là.
          suivi.interruption = refus.code !== 'REVISION_MISMATCH' ? refus : {
            code: 'STALE_CANVAS_REVISION',
            detail: `STALE_CANVAS_REVISION: the document changed just before a write on paragraph ${adresse.nodeId} in ${direLeLieu(lieu)}, and the editor refused that write `
              + `(${refus.detail}): the lawyer typed, or another write ran, during the call`,
          };
          return sorts;
        }
        if (suite.issue === 'sans_objet') sort.sansObjet.push(step.id);
        else sort.sautees.push({ stepId: step.id, refus });
      }
      // Une écriture directe ne rend pas la révision qu'elle laisse : elle est lue une fois, après la dernière. C'est
      // elle que la relecture du fichier doit retrouver, et que l'écriture suivante de l'appel attend.
      if (soumises > 0) {
        suivi.apresLEcriture = await revisionLue();
        suivi.attendue = suivi.apresLEcriture ?? suivi.attendue;
      }
      return sorts;
    },

    // Un lieu que la relecture ne retrouve pas (la partie a disparu pendant l'appel) ne juge rien.
    async relire(relecture) {
      if (!('acte' in relecture)) return relecture;
      const relu = await duLieu(relecture.acte);
      return 'absent' in relu ? { nonRelu: 'reread_failed' } : { acte: relu.acte };
    },

    faits: { lieu },
  };
}
